import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, isThirdPartyVirtualWarehouse, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData, WarehousingTransfer } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { WarehousingWorkHistoryComponent } from '../shipment-work/warehousing-work-history.component';
import { WarehousingDesktopOutboundComponent } from '../shipment-work/warehousing-desktop-outbound.component';
import { WarehousingSupplyReserveComponent } from '../shipment-work/warehousing-supply-reserve.component';
import { itemIdentityLabel } from '../warehousing-item-display';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';

@Component({
  selector: 'app-warehousing-transfer-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ProgressBarModule, TableModule, TagModule, ToastModule, PageDataPanelComponent, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent, FieldResourcesPanelComponent, WarehousingWorkHistoryComponent, WarehousingDesktopOutboundComponent, WarehousingSupplyReserveComponent],
  providers: [MessageService],
  templateUrl: './warehousing-transfer-view.component.html'
})
export class WarehousingTransferViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);

  isLoading = false;
  isSubmitting = false;
  transfer?: WarehousingTransfer;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Transfer Details', 'Transfers', '/warehousing/transfers');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  backLink(): string { return this.route.snapshot.queryParamMap.get('fromShipmentWork') === '1'
    ? this.workReturnLink() : '/warehousing/transfers'; }

  private workReturnLink(): string {
    const params = this.route.snapshot.queryParamMap;
    const type = params.get('workType');
    const id = params.get('workId');
    return type && id ? `/warehousing/shipment-work/${type}/${id}` : '/warehousing/shipment-work';
  }

  ngOnInit(): void {
    this.loadTransfer();
  }

  loadTransfer(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      transfer: this.warehousingService.getTransfer(id),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.transfer = response.transfer.data;
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.transfer?.transferNumber || 'Transfer Details', 'Transfers', '/warehousing/transfers');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  reserveTransfer(): void {
    if (!this.transfer?.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.reserveTransfer(this.transfer.id).pipe(finalize(() => this.isSubmitting = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Reserved', detail: 'Source stock units have been reserved.' });
      this.loadTransfer();
    });
  }

  cancelTransfer(): void {
    if (!this.transfer?.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.cancelTransfer(this.transfer.id).pipe(finalize(() => this.isSubmitting = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'Transfer order was cancelled.' });
      this.loadTransfer();
    });
  }

  clientName(id?: number): string { return this.clients.find(client => client.id === id)?.companyName || '-'; }
  accountName(id?: number): string { return this.accounts.find(account => account.id === id)?.companyName || '-'; }
  itemName(id?: number): string {
    const item = this.items.find(row => row.id === id);
    return itemIdentityLabel(item);
  }
  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return (this.masterData.WAREHOUSE || []).find(item => item.id === id);
  }

  usesVirtualWarehouse(): boolean {
    return isThirdPartyVirtualWarehouse(this.warehouseById(this.transfer?.sourceWarehouseId))
      || isThirdPartyVirtualWarehouse(this.warehouseById(this.transfer?.destinationWarehouseId));
  }

  metrics(): PageMetric[] {
    const lines = this.transfer?.lines || [];
    return [
      { label: 'Lines', value: lines.length },
      { label: 'Requested', value: lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0) },
      { label: 'Reserved', value: lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0), tone: 'warn' },
      { label: 'Dispatched', value: lines.reduce((sum, line) => sum + (line.quantityDispatched || 0), 0), tone: 'info' },
      { label: 'Received', value: lines.reduce((sum, line) => sum + (line.quantityReceived || 0), 0), tone: 'success' }
    ];
  }

  reservationRows(): Array<{ itemId: number; reservation: any }> {
    return (this.transfer?.lines || []).flatMap(line => (line.reservations || []).map(reservation => ({
      itemId: line.itemId,
      reservation
    })));
  }

  scanSummary(line: WarehousingTransfer['lines'][number]): string {
    const reservations = line.reservations || [];
    if (!reservations.length) return '-';
    const picked = reservations.filter(row => this.scanRank(row.scanStatus) >= 1).length;
    const staged = reservations.filter(row => this.scanRank(row.scanStatus) >= 2).length;
    const loaded = reservations.filter(row => this.scanRank(row.scanStatus) >= 3).length;
    const received = reservations.filter(row => (row.receiptStatus || '').toUpperCase() === 'RECEIVED').length;
    return `${picked}/${reservations.length} picked | ${staged}/${reservations.length} staged | ${loaded}/${reservations.length} loaded | ${received}/${reservations.length} received`;
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  }

  displayDateTime(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  canReserve(): boolean {
    const lines = this.transfer?.lines || [];
    const requested = lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0);
    const reserved = lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0);
    return !this.usesVirtualWarehouse()
      && (this.transfer?.status || '').toUpperCase() === 'PLANNED'
      && reserved < requested;
  }

  canChooseSupply(): boolean {
    return !this.usesVirtualWarehouse()
      && ['PLANNED', 'RESERVED'].includes((this.transfer?.status || '').toUpperCase());
  }

  supplyAutoOpen(): boolean { return this.route.snapshot.queryParamMap.get('reserveSupply') === '1'; }

  canScanOutbound(): boolean {
    return !this.usesVirtualWarehouse()
      && ['RESERVED', 'PICKED', 'STAGED', 'LOADED'].includes((this.transfer?.status || '').toUpperCase());
  }

  canConfirmDispatch(): boolean {
    return !this.usesVirtualWarehouse()
      && (this.transfer?.status || '').toUpperCase() === 'LOADED';
  }

  canReceive(): boolean {
    return !this.usesVirtualWarehouse()
      && ['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes((this.transfer?.status || '').toUpperCase());
  }

  canCancel(): boolean {
    return !['IN_TRANSIT', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'].includes((this.transfer?.status || '').toUpperCase());
  }

  gateStatusLabel(): string {
    if (!this.transfer?.gatedByTransferId) {
      return 'Ungated first leg';
    }
    return this.canReserve() || this.canScanOutbound() || this.canConfirmDispatch()
      ? 'Inbound receipt gate open'
      : 'Waiting for inbound receipt';
  }

  gateStatusSeverity(): 'success' | 'warn' | 'secondary' {
    if (!this.transfer?.gatedByTransferId) return 'secondary';
    return this.gateStatusLabel().includes('open') ? 'success' : 'warn';
  }

  private scanRank(scanStatus?: string): number {
    switch ((scanStatus || 'PENDING').toUpperCase()) {
      case 'PICKED': return 1;
      case 'STAGED': return 2;
      case 'LOADED': return 3;
      default: return 0;
    }
  }

  statusSeverity = statusSeverity;
}
